import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { graphqlMutation } from '../utils/graphqlApi';
import { generateEvalsReport } from '../graphql/mutations';
import useUserPermissions from '../hooks/useUserPermissions';
import EvalSessionDetailModal from './EvalSessionDetailModal';
import './AiGovernance.css';

const RAG_GREEN = 0.9;
const RAG_AMBER = 0.75;

const ragClass = (v) => {
  if (v == null) return 'ge-muted';
  if (v >= RAG_GREEN) return 'ge-good';
  if (v >= RAG_AMBER) return 'ge-warn';
  return 'ge-bad';
};

const ragColor = (v) => {
  if (v == null) return '#a4b0cc';
  if (v >= RAG_GREEN) return '#3ad19a';
  if (v >= RAG_AMBER) return '#f0b657';
  return '#f0716f';
};

const ragBg = (v) => {
  if (v == null) return '';
  if (v >= RAG_GREEN) return 'ge-bg-good';
  if (v >= RAG_AMBER) return 'ge-bg-warn';
  return 'ge-bg-bad';
};

// KPI card matching the dashboard's gradient + colored-stripe style.
// Hint/subtext line intentionally omitted per request — cards show only the
// label and value now.
function KpiCard({ label, value, stripeScore, variant }) {
  const stripeClassName = stripeScore != null ? ragBg(stripeScore) : '';
  const variantCls = variant === 'cost' ? 'ge-kpi-cost' : variant === 'users' ? 'ge-kpi-users' : '';
  return (
    <div className={`ge-kpi ${variantCls}`}>
      {stripeScore !== undefined && <span className={`ge-stripe ${stripeClassName}`} />}
      <div className="ge-kpi-label">{label}</div>
      <div className="ge-kpi-value">{value}</div>
    </div>
  );
}

const DEFAULT_WINDOWS = [
  { value: '24h', label: '24 Hours' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
];

const parseJsonSafely = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch (parseError) {
    console.error('[AiGovernanceAgentDetail] Failed to parse JSON payload:', parseError);
    return null;
  }
};

// Compact, log-safe summary of the report payload — mirrors the backend's
// per-window "Shape:" log so the two sides can be correlated when the page
// looks empty or the numbers seem wrong. Never throws.
const summarizeReport = (data, agentId) => {
  try {
    const windows = {};
    Object.entries(data?.windows || {}).forEach(([wkey, wval]) => {
      const summary = wval?.summary || {};
      const agents = wval?.agents || [];
      windows[wkey] = {
        total_agents: summary.total_agents ?? null,
        agent_table_rows: (summary.agent_table || []).length,
        // Whether THIS agent has quality data in the window — the key signal
        // for a per-agent page that renders empty.
        this_agent_present: agents.some((a) => a.agent_id === agentId),
      };
    });
    return { agentId, meta_windows: Object.keys(data?.meta?.windows || {}).length, windows };
  } catch (summaryError) {
    return { summary_error: String(summaryError) };
  }
};

const formatPct = (value, decimals = 1) => {
  if (value == null || !Number.isFinite(Number(value))) return 'N/A';
  return `${(Number(value) * 100).toFixed(decimals)}%`;
};

const formatRisk = (value) => {
  if (value == null || !Number.isFinite(Number(value))) return 'N/A';
  return Number(value).toFixed(2);
};

const usd = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return 'N/A';
  const n = Number(v);
  if (n >= 100) return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(4)}`;
};

const tokfmt = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return 'N/A';
  const n = Number(v);
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
};

const titleEval = (evalId) => (evalId || '').replace(/_/g, ' ');
const shortSkill = (s) => (s || '').replace('service-needs-', '');

const severityBadgeClass = (severity) => {
  const s = (severity || '').toLowerCase();
  if (s === 'critical' || s === 'high') return 'ge-sev-critical';
  if (s === 'medium') return 'ge-sev-medium';
  if (s === 'low') return 'ge-sev-low';
  return 'ge-sev-none';
};

function AiGovernanceAgentDetail() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { canAccessAiGovernance, getUserRole, isLoading: isPermissionsLoading } = useUserPermissions();

  const [timeWindow, setTimeWindow] = useState('7d');
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [source, setSource] = useState(null);

  // Which eval-detail rows are expanded (keyed by eval_id), matching the
  // reference report's per-eval "Details" toggle.
  const [openEvals, setOpenEvals] = useState({});
  // Session whose detail modal is open: { session, evalId }
  const [openSession, setOpenSession] = useState(null);
  const closeSession = useCallback(() => setOpenSession(null), []);

  const requestRef = useRef(0);

  const fetchReport = useCallback(async ({ forceRefresh = false } = {}) => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    const isStale = () => requestRef.current !== requestId;

    setIsLoading(true);
    setError(null);

    const t0 = (typeof performance !== 'undefined' ? performance : Date).now();
    // eslint-disable-next-line no-console
    console.log(
      `[AiGovernanceAgentDetail] generateEvalsReport → request (agentId=${agentId}, force_refresh=${forceRefresh}, requestId=${requestId})`
    );

    try {
      const result = await graphqlMutation(generateEvalsReport, {
        input: { force_refresh: forceRefresh },
      });
      if (isStale()) return;

      const rawResponse = result?.generateEvalsReport;
      const elapsedMs = Math.round((typeof performance !== 'undefined' ? performance : Date).now() - t0);
      // eslint-disable-next-line no-console
      console.log(
        `[AiGovernanceAgentDetail] generateEvalsReport ← response in ${elapsedMs}ms (statusCode=${rawResponse?.statusCode ?? 'n/a'})`,
        result
      );
      const responseBody = rawResponse?.body;
      if (!responseBody) {
        // eslint-disable-next-line no-console
        console.error('[AiGovernanceAgentDetail] No body on response:', rawResponse);
        setError('No report data returned.');
        return;
      }

      const parsed = parseJsonSafely(responseBody);
      if (!parsed) {
        // eslint-disable-next-line no-console
        console.error('[AiGovernanceAgentDetail] Failed to parse body:', responseBody);
        setError('Failed to parse report response.');
        return;
      }

      // Handle double-encoded body (same as dashboard — see comment there).
      const envelope = typeof parsed === 'string' ? parseJsonSafely(parsed) : parsed;
      if (!envelope || typeof envelope !== 'object') {
        // eslint-disable-next-line no-console
        console.error('[AiGovernanceAgentDetail] Body is not an object after parsing:', typeof parsed, parsed);
        setError('Failed to parse report response.');
        return;
      }

      // The body can arrive in two shapes:
      //   1. Envelope (current Lambda): { status, source, generated_at, data: {meta, windows} }
      //   2. Raw report (older Lambda or stale cache): { meta, windows, ... }
      // Detect which shape we received and normalize so the page works with both.
      const isEnvelope = typeof envelope.status === 'string';
      const isRawReport = !isEnvelope && envelope.meta && envelope.windows;

      // eslint-disable-next-line no-console
      console.log(
        `[AiGovernanceAgentDetail] parsed body: isEnvelope=${isEnvelope}, isRawReport=${isRawReport}, ` +
          `status=${envelope.status}, source=${envelope.source}, hasData=${Boolean(envelope.data)}, ` +
          `hasMeta=${Boolean(envelope.meta)}, hasWindows=${Boolean(envelope.windows)}`
      );

      // Envelope with an explicit error — surface the message.
      if (isEnvelope && (envelope.status === 'error' || !envelope.data)) {
        // eslint-disable-next-line no-console
        console.error('[AiGovernanceAgentDetail] Error envelope:', rawResponse, envelope);
        setError(envelope.error_message || `Request failed (status ${rawResponse?.statusCode ?? 'unknown'}).`);
        return;
      }

      // Extract the report data from whichever shape we got.
      const rd = isEnvelope ? envelope.data : isRawReport ? envelope : null;

      if (!rd || !rd.windows) {
        // eslint-disable-next-line no-console
        console.error('[AiGovernanceAgentDetail] Unrecognized payload shape:', rawResponse, envelope);
        setError('Unexpected report format — no windows data found.');
        return;
      }

      // eslint-disable-next-line no-console
      console.log('[AiGovernanceAgentDetail] report shape:', summarizeReport(rd, agentId));

      setReportData(rd);
      setSource(isEnvelope ? envelope.source || null : null);
    } catch (err) {
      if (isStale()) return;
      console.error('[AiGovernanceAgentDetail] Error fetching evals report:', err);
      setError(err.message || 'Unable to load GenAI evals report.');
    } finally {
      if (!isStale()) setIsLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    if (isPermissionsLoading || !canAccessAiGovernance()) return;
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermissionsLoading, fetchReport]);

  const meta = reportData?.meta || null;
  const windowLabels = meta?.windows || {};
  const windowOptions = DEFAULT_WINDOWS.map((w) => ({
    value: w.value,
    label: windowLabels[w.value] || w.label,
  }));

  const activeWindow = reportData?.windows?.[timeWindow] || null;
  const summary = activeWindow?.summary || null;

  const agentQuality = (activeWindow?.agents || []).find((a) => a.agent_id === agentId) || null;
  const agentTableRow = (summary?.agent_table || []).find((a) => a.agent_id === agentId) || null;
  // Display name comes from the backend report. Prefer the agent_table row (the
  // backend builds it from the agent registry, the source of truth for names)
  // over the per-window quality row, whose name is stamped on each eval record
  // when it is written and can lag behind a registry rename.
  const agentName = agentTableRow?.agent_name || agentQuality?.agent_name || agentId;

  // Voice / sentiment filtered to this agent
  const voice = summary?.voice || null;
  const agentSentiment = voice?.agent_sentiment?.[agentId] || null;
  const agentComments = (voice?.comments || []).filter((c) => c.agent_id === agentId);

  // Security filtered to this agent
  const security = summary?.security || null;
  const agentSecurity = (security?.agents || []).find((a) => a.agent_id === agentId) || null;
  const agentFlaggedSessions = (security?.flagged_sessions || []).filter((s) => s.agent_id === agentId);

  // Feedback (unique users / sessions-per-user) filtered to this agent.
  const feedback = summary?.feedback || null;
  const agentUniqueUsers = feedback?.agent_users?.[agentId]?.length ?? null;
  const agentFeedbackSessions = feedback?.agent_sessions?.[agentId] ?? null;
  const agentSessionsPerUser =
    agentUniqueUsers && agentFeedbackSessions != null ? agentFeedbackSessions / agentUniqueUsers : null;

  // Cost filtered to this agent (used for the Cost section moved here from
  // the main dashboard).
  const costData = summary?.cost || null;
  const agentCost = costData?.has_cost ? (costData.agents || []).find((a) => a.agent_id === agentId) : null;
  const agentCostChartData = (agentCost?.trend || []).map((p) => ({
    label: p.label || p.bucket,
    cost: p.cost ?? 0,
    tokens: p.tokens ?? 0,
  }));

  // Per-agent KPI values for the card row (mirrors the fleet-wide KPIs on the
  // main dashboard, but every number here is scoped to just this agent).
  const kpiAccuracy = agentTableRow?.accuracy ?? agentQuality?.overall_score ?? null;
  const kpiSentimentPct = agentSentiment?.total ? (agentSentiment.positive ?? 0) / agentSentiment.total : null;

  const toggleEval = (evalId) => {
    setOpenEvals((prev) => ({ ...prev, [evalId]: !prev[evalId] }));
  };

  if (isPermissionsLoading) {
    return (
      <div className="genai-evals-dark container-fluid py-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2 mb-0">Checking permissions...</p>
      </div>
    );
  }

  if (!canAccessAiGovernance()) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Access Denied</h4>
          <p className="mb-1">Your role does not have access to AI Governance.</p>
          <p className="mb-0">
            <strong>Current role:</strong> {getUserRole()}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="genai-evals-dark container-fluid py-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
        <div>
          <button
            type="button"
            className="btn btn-sm btn-link text-decoration-none ps-0 mb-1"
            onClick={() => navigate('/genai-evals')}
          >
            <i className="bi bi-arrow-left me-1" aria-hidden="true"></i>
            Back to AI Governance
          </button>
          <h4 className="mb-0">{agentName}</h4>
        </div>
        <div className="d-flex align-items-center gap-2">
          <div className="btn-group" role="group" aria-label="Time window">
            {windowOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`btn btn-sm ${timeWindow === opt.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setTimeWindow(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {source && (
            <span className="text-muted" style={{ fontSize: '0.8rem' }}>
              {source === 'cache' ? 'Served from cache' : 'Freshly computed'}
            </span>
          )}
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => fetchReport({ forceRefresh: true })}
            disabled={isLoading}
          >
            <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
          <span>{error}</span>
          <button className="btn btn-sm btn-outline-danger" onClick={() => fetchReport()}>
            Retry
          </button>
        </div>
      )}

      {isLoading && !reportData && (
        <div className="card mb-4">
          <div className="card-body">
            <div className="placeholder-glow">
              <span className="placeholder col-3 mb-2"></span>
              <span className="placeholder col-12" style={{ height: '80px' }}></span>
            </div>
          </div>
        </div>
      )}

      {!isLoading && !error && reportData && (
        <>
          {!agentQuality && !agentTableRow && (
            <div className="alert alert-warning">No data found for agent "{agentId}" in this window.</div>
          )}

          {/* Agent KPI cards — same gradient/stripe style as the main dashboard,
              scoped to this agent only. "Agents with evals" is intentionally
              omitted (fleet-only metric, not meaningful for a single agent). */}
          {(agentTableRow || agentQuality) && (
            <div className="ge-kpis">
              <KpiCard
                label="Overall Accuracy"
                value={<span className={ragClass(kpiAccuracy)}>{formatPct(kpiAccuracy)}</span>}
                stripeScore={kpiAccuracy}
              />
              <KpiCard label="Users" value={agentUniqueUsers ?? 'N/A'} variant="users" />
              <KpiCard label="Total Sessions" value={agentFeedbackSessions ?? 'N/A'} variant="users" />
              <KpiCard label="User Sentiment" value={kpiSentimentPct != null ? formatPct(kpiSentimentPct, 0) : 'N/A'} />
            </div>
          )}

          {/* ═══ Security & risks (moved to top) ═══ */}
          {security?.has_security && (
            <>
              <h2 className="ge-section">Security &amp; risks</h2>
              <div className="ge-kpis">
                <KpiCard label="Sessions Scanned" value={agentSecurity?.scanned ?? 0} />
                <KpiCard
                  label="Malicious Sessions"
                  value={<span className={agentSecurity?.flagged ? 'ge-bad' : 'ge-good'}>{agentSecurity?.flagged ?? 0}</span>}
                />
                <KpiCard label="Avg Risk Score" value={formatRisk(agentSecurity?.avg_risk)} />
              </div>
              <div className="card">
                <h3>Flagged sessions</h3>
                {agentFlaggedSessions.length === 0 ? (
                  <div className="ge-empty">No malicious sessions detected for this agent in this window.</div>
                ) : (
                  <div style={{ overflowX: 'auto', maxHeight: 360 }}>
                    <table>
                      <thead>
                        <tr>
                          <th>When</th>
                          <th>Session</th>
                          <th>Severity</th>
                          <th>Risk score</th>
                          <th>Attack types</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agentFlaggedSessions.slice(0, 20).map((s, idx) => (
                          <tr key={s.session_id || idx}>
                            <td style={{ whiteSpace: 'nowrap' }}>{s.when || 'N/A'}</td>
                            <td className="ge-skill">{s.session_id}</td>
                            <td>
                              <span
                                className={severityBadgeClass(s.severity)}
                                style={{ padding: '2px 10px', borderRadius: 20, fontSize: '12.5px', fontWeight: 600 }}
                              >
                                {s.severity || 'none'}
                              </span>
                            </td>
                            <td className="ge-score-num">{formatRisk(s.risk_score)}</td>
                            <td>
                              {(s.attack_types || []).map((t, i) => (
                                <span key={i} className="ge-atk-chip">
                                  {t.replace(/_/g, ' ')}
                                </span>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ═══ Agent scorecard — matches reference agentTable() + agentDetail() exactly ═══ */}
          {agentQuality && (agentQuality.evals || []).length > 0 && (
            <div className="card">
              <h3>Agent scorecards</h3>
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Agent</th>
                      <th>Type of Evals</th>
                      <th>Sessions</th>
                      <th>Eval accuracy</th>
                      <th>Overall</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Single agent row */}
                    <tr>
                      <td>
                        <span className="ge-rag-dot" style={{ background: ragColor(agentQuality.overall_score) }} />
                      </td>
                      <td>
                        <div className="ge-agent-name">{agentName}</div>
                        <div className="ge-muted ge-skill">{agentQuality.agent_key || agentQuality.agent_id}</div>
                        {agentTableRow?.agent_pattern && (
                          <div className="ge-muted ge-skill">{agentTableRow.agent_pattern}</div>
                        )}
                      </td>
                      <td>{agentQuality.eval_count}</td>
                      <td>
                        {agentQuality.sessions}
                        {agentQuality.low_sample && <span className="ge-sample-flag">low sample</span>}
                      </td>
                      <td>
                        {agentQuality.evals.map((e) => (
                          <span key={e.eval_id} className="ge-chip">
                            <span className="ge-rag-dot" style={{ background: ragColor(e.score) }} />
                            {titleEval(e.eval_id)}
                            <span className={`ge-score-num ${ragClass(e.score)}`}>{formatPct(e.score, 0)}</span>
                            <span className="ge-etype">{e.eval_type}</span>
                          </span>
                        ))}
                      </td>
                      <td>
                        <span className={`ge-score-num ${ragClass(agentQuality.overall_score)}`}>
                          {formatPct(agentQuality.overall_score)}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="ge-btn"
                          onClick={() => toggleEval('__agent__')}
                        >
                          {openEvals['__agent__'] ? 'Hide' : 'Details'}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded detail row — one block per eval */}
                    {openEvals['__agent__'] && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0, border: 'none' }}>
                          <div style={{ marginTop: 16, borderTop: '1px dashed #2a3550', paddingTop: 16 }}>
                            {agentQuality.evals.map((evalItem) => {
                              const chartData = (evalItem.trend || []).map((point) => ({
                                label: point.label || point.bucket,
                                score: point.score != null ? +(point.score * 100).toFixed(1) : null,
                                count: point.count ?? 0,
                              }));
                              return (
                                <div key={evalItem.eval_id} style={{ marginBottom: 20 }}>
                                  {/* Eval header */}
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                                    <div style={{ fontWeight: 700, fontSize: '15.5px' }}>
                                      {titleEval(evalItem.eval_id)}
                                      <span className="ge-muted" style={{ fontWeight: 400 }}>
                                        {' '}({evalItem.eval_type}, {evalItem.sessions} sessions)
                                      </span>
                                      {evalItem.low_sample && <span className="ge-sample-flag">low sample</span>}
                                    </div>
                                    <div>
                                      <span className={`ge-score-num ${ragClass(evalItem.score)}`}>
                                        {formatPct(evalItem.score)}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Feedback summary */}
                                  {evalItem.feedback && evalItem.feedback.total ? (
                                    <div className="ge-fb-summary">
                                      User feedback:{' '}
                                      <span className="ge-good">{evalItem.feedback.positive} positive</span>
                                      <span className="ge-sep">|</span>
                                      <span>{evalItem.feedback.neutral} neutral</span>
                                      <span className="ge-sep">|</span>
                                      <span className="ge-bad">{evalItem.feedback.negative} negative</span>
                                      {evalItem.feedback.with_text ? (
                                        <>
                                          <span className="ge-sep">|</span>
                                          <span className="ge-muted">{evalItem.feedback.with_text} with comments</span>
                                        </>
                                      ) : null}
                                    </div>
                                  ) : (
                                    <div className="ge-fb-summary ge-muted">
                                      User feedback: <b>none</b> on these sessions.
                                    </div>
                                  )}

                                  {/* Routing stats */}
                                  {evalItem.eval_type === 'routing' && (
                                    <div className="ge-muted" style={{ marginBottom: 10 }}>
                                      Strict correct {formatPct(evalItem.strict_correct, 0)} · misroutes{' '}
                                      {evalItem.misroute_count ?? 0} · ambiguous {evalItem.ambiguous_count ?? 0}
                                    </div>
                                  )}

                                  {/* Dimension bars (agent eval only) */}
                                  {evalItem.eval_type === 'agent' && evalItem.dimensions && (
                                    <div className="ge-dims">
                                      {Object.entries(evalItem.dimensions).map(([k, v]) => (
                                        <div key={k} className="ge-dim">
                                          <div>
                                            {k.replace(/_/g, ' ')}
                                            <span className={`ge-score-num ${ragClass(v)}`} style={{ float: 'right' }}>
                                              {v != null ? formatPct(v, 0) : 'N/A'}
                                            </span>
                                          </div>
                                          <div className="ge-dim-bar">
                                            <span style={{ width: `${v == null ? 0 : (v * 100).toFixed(0)}%`, background: ragColor(v) }} />
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Per-eval trend chart — line (Accuracy) + bar (Sessions), dual axis */}
                                  {chartData.length > 0 ? (
                                    <div style={{ height: 220 }}>
                                      <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart data={chartData} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(42,53,80,.5)" />
                                          <XAxis dataKey="label" tick={{ fontSize: 14, fill: '#a4b0cc' }} stroke="rgba(42,53,80,.5)" />
                                          <YAxis yAxisId="y" domain={[0, 100]} tickFormatter={(v) => v + '%'} tick={{ fontSize: 14, fill: '#a4b0cc' }} stroke="rgba(42,53,80,.5)" />
                                          <YAxis yAxisId="y1" orientation="right" tick={{ fontSize: 14, fill: '#a4b0cc' }} stroke="rgba(42,53,80,.5)" />
                                          <Tooltip
                                            formatter={(value, name) => name === 'Accuracy' ? value + '%' : value}
                                            contentStyle={{ backgroundColor: '#1e2740', border: '1px solid #2a3550', color: '#eef2fb' }}
                                            labelStyle={{ color: '#eef2fb' }}
                                          />
                                          <Legend wrapperStyle={{ color: '#a4b0cc' }} iconSize={12} />
                                          <Bar yAxisId="y1" dataKey="count" name="Sessions" fill="rgba(164,176,204,.35)" radius={[4, 4, 0, 0]} />
                                          <Line yAxisId="y" type="monotone" dataKey="score" name="Accuracy" stroke="#4f8cff" strokeWidth={2} dot={false} />
                                        </ComposedChart>
                                      </ResponsiveContainer>
                                    </div>
                                  ) : (
                                    <div className="ge-muted">No trend data available.</div>
                                  )}

                                  {/* Lowlights */}
                                  {!evalItem.lowlights || evalItem.lowlights.length === 0 ? (
                                    <div className="ge-muted" style={{ marginTop: 8 }}>
                                      No sessions below {formatPct(RAG_GREEN, 0)} — clean window.
                                    </div>
                                  ) : (
                                    <div className="ge-lowlights" style={{ marginTop: 12 }}>
                                      <div className="ge-muted" style={{ marginTop: 10, fontWeight: 600 }}>
                                        Needs attention — {Math.min(evalItem.lowlights.length, 10)} lowest-scoring session(s)
                                        {evalItem.lowlights.length > 10 ? ` (of ${evalItem.lowlights.length})` : ''}
                                      </div>
                                      <table>
                                        <thead>
                                          <tr>
                                            <th>When</th>
                                            <th>Session</th>
                                            <th>Score</th>
                                            {evalItem.eval_type === 'routing' && (
                                              <>
                                                <th>Routed to</th>
                                                <th>Expected</th>
                                              </>
                                            )}
                                            <th>Feedback</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {evalItem.lowlights.slice(0, 10).map((low, idx) => (
                                            <tr key={low.session_id || idx}>
                                              <td style={{ whiteSpace: 'nowrap' }}>{low.when || 'N/A'}</td>
                                              <td className="ge-skill">
                                                <button
                                                  type="button"
                                                  className="ge-link-btn"
                                                  title="Open session details"
                                                  onClick={() => setOpenSession({ session: low, evalId: evalItem.eval_id })}
                                                >
                                                  {low.session_id}
                                                </button>
                                              </td>
                                              <td>
                                                <span className={`ge-score-num ${ragClass(low.score)}`}>
                                                  {low.score != null ? formatPct(low.score, 0) : 'N/A'}
                                                </span>
                                              </td>
                                              {evalItem.eval_type === 'routing' && (
                                                <>
                                                  <td>
                                                    <span className="ge-pill ge-routed ge-skill">{shortSkill(low.routed_skill) || '—'}</span>
                                                  </td>
                                                  <td>
                                                    <span className="ge-pill ge-expected ge-skill">{shortSkill(low.expected_skill) || '—'}</span>
                                                  </td>
                                                </>
                                              )}
                                              <td>
                                                {low.sentiment ? (
                                                  <span className={`ge-pill ge-sent-${low.sentiment}`}>{low.sentiment}</span>
                                                ) : (
                                                  !low.feedback_text && <span className="ge-muted">—</span>
                                                )}
                                                {low.feedback_text && <div className="ge-fb-text">{low.feedback_text}</div>}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ Cost (moved here from the main dashboard) ═══ */}
          <h2 className="ge-section">Cost</h2>
          {!agentCost ? (
            <div className="card">
              <div className="ge-empty">No cost data for this agent in this window.</div>
            </div>
          ) : (
            <div className="card">
              <h3>Spend over time</h3>
              {agentCostChartData.length === 0 ? (
                <div className="ge-empty">No cost trend data available for this agent.</div>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={agentCostChartData}
                    margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                    barCategoryGap="20%"
                    barGap={4}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(42,53,80,.5)" />
                    <XAxis dataKey="label" tick={{ fontSize: 14, fill: '#a4b0cc' }} stroke="rgba(42,53,80,.5)" />
                    <YAxis yAxisId="cost" tickFormatter={(v) => `$${v}`} tick={{ fontSize: 14, fill: '#a4b0cc' }} stroke="rgba(42,53,80,.5)" />
                    <Tooltip
                      formatter={(value, name) => (name === 'Cost ($)' ? usd(value) : tokfmt(value))}
                      contentStyle={{ backgroundColor: '#1e2740', border: '1px solid #2a3550', color: '#eef2fb' }}
                      labelStyle={{ color: '#eef2fb' }}
                    />
                    <Legend wrapperStyle={{ color: '#a4b0cc' }} />
                    <Bar
                      yAxisId="cost"
                      dataKey="cost"
                      name="Cost ($)"
                      fill="rgba(198,160,255,.55)"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={64}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          )}

          {/* ═══ Adoption & usage — engagement table only, no chart ═══ */}
          {feedback?.has_feedback && (
            <>
              <h2 className="ge-section">Adoption &amp; usage</h2>
              <div className="card">
                <h3>Engagement detail</h3>
                {agentUniqueUsers == null ? (
                  <div className="ge-empty">No user activity for this agent.</div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Agent</th>
                        <th>Unique users</th>
                        <th>Feedback sessions</th>
                        <th>Sessions / user</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <div className="ge-agent-name">{agentName}</div>
                        </td>
                        <td className="ge-score-num">{agentUniqueUsers}</td>
                        <td>{agentFeedbackSessions ?? 0}</td>
                        <td>{agentSessionsPerUser != null ? agentSessionsPerUser.toFixed(1) : 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {/* ═══ Voice of the user — same as main page ═══ */}
          {voice?.has_voice && (
            <>
              <h2 className="ge-section">Voice of the user</h2>
              {(() => {
                const pos = agentSentiment?.positive || 0;
                const neu = agentSentiment?.neutral || 0;
                const neg = agentSentiment?.negative || 0;
                const total = pos + neu + neg;
                if (!total) {
                  return (
                    <div className="card">
                      <div className="ge-empty">No sentiment data for this agent.</div>
                    </div>
                  );
                }
                const pw = (pos / total) * 100;
                const nw = (neu / total) * 100;
                const gw = (neg / total) * 100;
                return (
                  <div className="card">
                    <h3>Sentiment split</h3>
                    <div className="ge-sent-bar">
                      {pos > 0 && <span style={{ width: `${pw}%`, background: '#3ad19a' }} title={`Positive ${pos}`} />}
                      {neu > 0 && <span style={{ width: `${nw}%`, background: '#5b6684' }} title={`Neutral ${neu}`} />}
                      {neg > 0 && <span style={{ width: `${gw}%`, background: '#f0716f' }} title={`Negative ${neg}`} />}
                    </div>
                    <div className="ge-sent-legend">
                      <span>
                        <i className="ge-sdot" style={{ background: '#3ad19a' }} /> Positive <b>{pos}</b>{' '}
                        <span className="ge-muted">({pw.toFixed(0)}%)</span>
                      </span>
                      <span>
                        <i className="ge-sdot" style={{ background: '#5b6684' }} /> Neutral <b>{neu}</b>{' '}
                        <span className="ge-muted">({nw.toFixed(0)}%)</span>
                      </span>
                      <span>
                        <i className="ge-sdot" style={{ background: '#f0716f' }} /> Negative <b>{neg}</b>{' '}
                        <span className="ge-muted">({gw.toFixed(0)}%)</span>
                      </span>
                    </div>
                  </div>
                );
              })()}

              {/* Written feedback — renamed from "What users are saying", score removed. */}
              {agentComments.length > 0 && (
                <div className="card">
                  <h3>Written feedback</h3>
                  <div className="ge-comments">
                    {agentComments.slice(0, 20).map((c, idx) => (
                      <div
                        key={c.session_id || idx}
                        className={`ge-comment ${c.sentiment === 'positive' ? 'ge-positive' : c.sentiment === 'negative' ? 'ge-negative' : ''}`}
                      >
                        <div className="ge-comment-head">
                          <span
                            className="ge-rag-dot"
                            style={{
                              background: c.sentiment === 'positive' ? '#3ad19a' : c.sentiment === 'negative' ? '#f0716f' : '#5b6684',
                            }}
                          />
                          <span className="ge-muted" style={{ textTransform: 'capitalize' }}>
                            {c.sentiment}
                          </span>
                          <span className="ge-muted" style={{ marginLeft: 'auto' }}>
                            {c.when}
                          </span>
                        </div>
                        <div className="ge-comment-text">{c.text}</div>
                        {c.session_id && (
                          <div className="ge-skill ge-muted" style={{ marginTop: 6 }}>
                            {c.session_id}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      <EvalSessionDetailModal
        session={openSession?.session}
        evalId={openSession?.evalId}
        agentId={agentQuality?.agent_id || agentId}
        onClose={closeSession}
      />
    </div>
  );
}

export default AiGovernanceAgentDetail;
